import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Sparkles } from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const AREAS = ['roi', 'talent', 'risks', 'training', 'general'];
const PERIODS = ['current_q', 'previous_q', 'last_year'];
const URGENCIES = ['low', 'medium', 'high'];

export default function StrategicAnalysisDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    area: 'general',
    period: 'current_q',
    description: '',
    urgency: 'medium',
  });
  const [saving, setSaving] = useState(false);

  const reset = () => setForm({ area: 'general', period: 'current_q', description: '', urgency: 'medium' });

  const handleSubmit = useCallback(async () => {
    if (!form.description.trim()) {
      toast.error(t('clientTalentIntelligence.strategicDialog.descRequired'));
      return;
    }
    setSaving(true);
    try {
      await clientApi.requestStrategicAnalysis({
        area: form.area,
        period: form.period,
        description: form.description.trim(),
        urgency: form.urgency,
      });
      toast.success(t('clientTalentIntelligence.strategicDialog.sentTitle'), {
        description: t('clientTalentIntelligence.strategicDialog.sentBody'),
      });
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(t('clientTalentIntelligence.strategicDialog.failedTitle'), {
        description: formatApiError(e),
      });
    } finally { setSaving(false); }
  }, [form, t, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="cti-strategic-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            {t('clientTalentIntelligence.strategicDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('clientTalentIntelligence.strategicDialog.subtitle')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>{t('clientTalentIntelligence.strategicDialog.area')}</Label>
              <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                <SelectTrigger data-testid="cti-strategic-area"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AREAS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {t(`clientTalentIntelligence.strategicDialog.areas.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>{t('clientTalentIntelligence.strategicDialog.period')}</Label>
              <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                <SelectTrigger data-testid="cti-strategic-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIODS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {t(`clientTalentIntelligence.strategicDialog.periods.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>{t('clientTalentIntelligence.strategicDialog.description')}</Label>
            <Textarea
              rows={4}
              value={form.description}
              placeholder={t('clientTalentIntelligence.strategicDialog.descriptionPh')}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="cti-strategic-description"
            />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('clientTalentIntelligence.strategicDialog.urgency')}</Label>
            <div className="flex gap-2" data-testid="cti-strategic-urgency-row">
              {URGENCIES.map((u) => (
                <Button
                  key={u}
                  type="button"
                  size="sm"
                  variant={form.urgency === u ? 'default' : 'outline'}
                  onClick={() => setForm({ ...form, urgency: u })}
                  data-testid={`cti-strategic-urgency-${u}`}
                >
                  {t(`clientTalentIntelligence.strategicDialog.urgencies.${u}`)}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
                  data-testid="cti-strategic-cancel">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="cti-strategic-submit">
            {saving
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : t('clientTalentIntelligence.strategicDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
